// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SecurityManager.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
// $FlowFixMe
import ensure from 'ensure';
// $FlowFixMe
import { filter, find, forEach, get, getOr, isEmpty, isEqual, isFunction, isNil, isObject, has } from 'lodash/fp';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import UiState from 'logic/Izenda.UiState';
import IntegrationSetting from 'logic/models/Izenda.IntegrationSetting';
import { licenseInfoInstance } from 'logic/models/Izenda.LicenseInfo';
import SecurityDetail from 'logic/models/Izenda.SecurityDetail';
import { computed } from 'mobx';
import AuthUtils from 'share/utils/Izenda.AuthUtils';
import typeof RouteUtils from 'share/utils/Izenda.RouteUtils';
import queryString from 'query-string';
import uiState from 'logic/Izenda.UiState';

export default class SecurityManager {
  @computed
  get isCompleteLoad(): boolean {
    return SecurityDetail.isLoaded;
  }

  /**
   * Set access token
   * @param {String} token
   */
  setAccessToken(token: string): void {
    AuthUtils.setAccessToken(token);
  }

  /**
   * Get access token
   * @returns {String} access token
   */
  getAccessToken(): string {
    return AuthUtils.getAccessToken();
  }

  /**
   * Remove authorization cookie
   */
  removeAuthCookies(): void {
    AuthUtils.removeAuthCookies();
  }

  /**
   * Clear data
   */
  clear(): void {
    this.removeAuthCookies();
    SecurityDetail.clear();
  }

  /**
   * Clear permission
   */
  clearPermission(): void {
    SecurityDetail.clear();
  }

  /**
   * Check privileges
   * @param {Array} keys array which has 3 types: array of string, array of object(left, operator, right) and function.
   * @param {String} operator
   * @returns {Boolean} has privileges
   */
  checkPrivileges(keys: any, operator: string): any {
    ensure(keys, 'permission keys must be set');

    let hasPrivilege;
    const permission = SecurityDetail.permission;

    if (isNil(permission) || isNil(this.getAccessToken())) {
      return null;
    } else {
      if (isFunction(keys)) {
        return keys.call(this, permission);
      }

      forEach(key => {
        if (isObject(key)) {
          if (isNil(hasPrivilege)) {
            hasPrivilege = this.checkPrivilegesByObject(key, permission);
          } else {
            if (!isNil(operator) && operator === AuthorizationConstants.PERMISSION_OPERATOR.Or) {
              hasPrivilege = hasPrivilege || this.checkPrivilegesByObject(key, permission);
            } else {
              hasPrivilege = hasPrivilege && this.checkPrivilegesByObject(key, permission);
            }
          }
        } else {
          if (isNil(hasPrivilege)) {
            hasPrivilege = this.checkPrivilegesByKey(key, permission);
          } else {
            if (!isNil(operator) && operator === AuthorizationConstants.PERMISSION_OPERATOR.Or) {
              hasPrivilege = hasPrivilege || this.checkPrivilegesByKey(key, permission);
            } else {
              hasPrivilege = hasPrivilege && this.checkPrivilegesByKey(key, permission);
            }
          }
        }
      }, keys);

      return hasPrivilege;
    }
  }

  /**
   * Check privileges by object type
   * @param {String} permission key
   * @param {Object} permission data
   * @returns {Boolean} has privileges
   */
  checkPrivilegesByObject(key: any, permission: any): boolean {
    let hasLeftPrivilege = false;
    if (isObject(key.left)) {
      hasLeftPrivilege = this.checkPrivilegesByObject(key.left, permission);
    } else {
      hasLeftPrivilege = get(key.left, permission);
    }

    let hasRightPrivilege = false;
    if (isObject(key.right)) {
      hasRightPrivilege = this.checkPrivilegesByObject(key.right, permission);
    } else {
      hasRightPrivilege = get(key.right, permission);
    }

    const operator = key.operator;
    if (operator === AuthorizationConstants.PERMISSION_OPERATOR.Or) {
      return hasLeftPrivilege || hasRightPrivilege;
    } else {
      return hasLeftPrivilege && hasRightPrivilege;
    }
  }

  /**
   * Check privileges by key
   * @param {String} permission key
   * @param {Object} permission data
   * @returns {Boolean} has privileges
   */
  checkPrivilegesByKey(key: string, permission: any): any {
    return get(key, permission);
  }

  /**
   * Check login status.
   */
  checkLoginStatus() {
    return !isNil(AuthUtils.getAccessToken());
  }

  /**
   * Check is system running under integration mode.
   */
  isIntegrationMode() {
    return IntegrationSetting.isIntegrationMode;
  }

  /**
   * Check license
   */
  checkLicense(key: any): any {
    ensure(key, 'license key must be set');
    const licenseModules = UiState.currentUser.currentModules;
    if (isNil(licenseModules) || isNil(this.getAccessToken())) {
      return null;
    } else {
      if (isFunction(key)) {
        return key.call(this, licenseModules);
      }

      const hasLicense = !isEmpty(
        filter(module => {
          return isEqual(module, key);
        }, licenseModules)
      );
      
      return hasLicense;
    }
  }

  /**
   * Get default link
   */
  getDefaultLink(routeUtils: RouteUtils, noLicenseSetup: boolean = false): string {
    const links = [
      { 
        key: LicenseCheckerConstants.LICENSE_KEY.Nlq,
        url: routeUtils.getRouteUrl(routeUtils.getConfigRoute('Explore')) 
      },
      {
        key: LicenseCheckerConstants.LICENSE_KEY.ReportDesigner,
        url: routeUtils.getRouteUrl(routeUtils.getConfigRoute('Report'))
      },
      {
        key: LicenseCheckerConstants.LICENSE_KEY.Dashboard,
        url: routeUtils.getRouteUrl(routeUtils.getConfigRoute('Dashboard'))
      },
      { url: routeUtils.getRouteUrl(routeUtils.getConfigRoute('Settings')) }
    ];

    const accessToken = AuthUtils.getAccessToken();
    const licenseModules = licenseInfoInstance.modules;
    if (isNil(accessToken)) {
      return routeUtils.getRouteUrl(routeUtils.getConfigRoute('Account', 'Login'));
    } else {
      if (isNil(licenseModules) || noLicenseSetup) {
        return routeUtils.getRouteUrl(routeUtils.getConfigRoute('Settings'));
      } else {
        for (let i = 0; i < links.length; i++) {
          const link = find(module => {
            return isNil(get('key', links[i])) || isEqual(module.name, get('key', links[i]));
          }, licenseModules);
          if (!isNil(link)) {
            return links[i].url;
          }
        }
      }
    }

    return routeUtils.getRouteUrl(routeUtils.getConfigRoute('Explore'));
  
  }

  /**
   * Get auto auth token
   */
  getAutoAuthToken(): string {
    const params = queryString.parse(window.location.search);
    return getOr(null, 'token', params);
  }

  /**
   * Authenticate with auto auth token
   */
  authWithAutoAuthToken(autoAuthToken: string, successCallback: any, failCallback: any): void {
    const authFetcher = require('fetchers/Izenda.UserAccountDataFetcher');
    const autoAuthTokenDetail = {
      autoAuthToken: autoAuthToken
    };
    authFetcher.autologin(autoAuthTokenDetail)
      .then(result => {
        const token = get('data.token', result);
        if (result.success && !isNil(token)) {
          AuthUtils.setAccessToken(token);
          uiState.setNeedLogin(false);
          isFunction(successCallback) && successCallback();
        } else {
          isFunction(failCallback) && failCallback();
        }
      })
      .catch(error => {
        isFunction(failCallback) && failCallback(error);
      });
  }
}



// WEBPACK FOOTER //
// ./ui/share/authorization/Izenda.SecurityManager.js